import * as React from 'react'
import { formatPrice } from 'helpers'
import { PricingSuggestion } from '../common'

const TrendingUp = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="2 17 8.5 10.5 13.5 15.5 22 7" />
    <polyline points="16 7 22 7 22 13" />
  </svg>
)

export interface PricingGuidanceSliderProps {
  suggestion: PricingSuggestion | null
  minimumPrice: number
  lowestQueuePrice: number | null
  initialValue?: number
  currencyCode?: string
  onPriceSelect?: (price: number) => void
  /** How far below/above last sold the slider extends (default 0.05 = ±5%) */
  rangePercent?: number
  /** Price points where the handle should snap when dragged nearby */
  snapPoints?: number[]
  /** Distance (in price units) within which snapping activates (default 2) */
  snapThreshold?: number
}

const PricingGuidanceSlider: React.FC<PricingGuidanceSliderProps> = ({
  suggestion,
  minimumPrice,
  lowestQueuePrice,
  initialValue,
  currencyCode = 'GBP',
  onPriceSelect,
  rangePercent = 0.05,
  snapPoints,
  snapThreshold = 2
}) => {
  // Compute sell quicker price — same logic as SuggestedPricing
  const belowLastSold = suggestion
    ? Math.round(suggestion.price * (1 - rangePercent))
    : minimumPrice
  const basePrice =
    suggestion &&
    lowestQueuePrice !== null &&
    belowLastSold >= lowestQueuePrice
      ? Math.round(lowestQueuePrice * (1 - rangePercent))
      : belowLastSold
  const sellQuickerPrice = Math.max(basePrice, minimumPrice)
  const lastSoldPrice = suggestion?.price ?? minimumPrice
  const minPrice = sellQuickerPrice
  const maxPrice = Math.round(lastSoldPrice * (1 + rangePercent))
  const range = maxPrice - minPrice

  const [value, setValue] = React.useState(initialValue ?? minPrice)
  const [isDragging, setIsDragging] = React.useState(false)
  const trackRef = React.useRef<HTMLDivElement>(null)

  // Sync slider when suggestion changes (new size) or when stepper updates the price externally
  React.useEffect(() => {
    if (suggestion) {
      setValue(Math.max(minPrice, Math.min(maxPrice, initialValue ?? minPrice)))
    }
  }, [suggestion?.price, minPrice, maxPrice, initialValue])

  // Don't render until we have a meaningful range
  if (!suggestion || range <= 0) return null

  const effectiveValue = Math.max(minPrice, Math.min(maxPrice, value))
  const pct = ((effectiveValue - minPrice) / range) * 100
  const tooltipPct = Math.max(8, Math.min(92, pct))
  const lastSoldPct = 50 // last sold is always centred on the slider

  const valueFromClientX = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect()

    if (!rect) return effectiveValue
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))

    return Math.round(minPrice + ratio * range)
  }

  const setAndNotify = (newValue: number) => {
    const snapped =
      snapPoints?.find((sp) => Math.abs(newValue - sp) <= snapThreshold) ??
      newValue
    setValue(snapped)
    onPriceSelect?.(snapped)
  }

  const isSnapped = snapPoints?.some((sp) => effectiveValue === sp) ?? false

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* synthetic events */ }
    setIsDragging(true)
    setAndNotify(valueFromClientX(e.clientX))
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return
    if (e.buttons === 0) {
      setIsDragging(false)
      return
    } // button released outside window

    setAndNotify(valueFromClientX(e.clientX))
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    setIsDragging(false)
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* synthetic events */ }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      setAndNotify(Math.max(minPrice, effectiveValue - 1))
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      setAndNotify(Math.min(maxPrice, effectiveValue + 1))
    }
  }

  return (
    <div className="pricing-guidance">
      {/* Header */}
      <div className="pricing-guidance__header">
        <span className="pricing-guidance__title">Pricing Guidance</span>
        <button
          type="button"
          className="pricing-guidance__help-btn"
          aria-label="Pricing guidance information"
        >
          <TrendingUp />
        </button>
      </div>

      {/* Slider area */}
      <div className="pricing-guidance__slider-area">
        {/* Floating price indicator — moves with handle */}
        <div
          className="pricing-guidance__tooltip"
          style={{ left: `${tooltipPct}%` }}
          aria-hidden="true"
        >
          <span className="pricing-guidance__tooltip-price">
            {formatPrice(effectiveValue, { currencyCode })}
          </span>
          <span className="pricing-guidance__tooltip-arrow" />
        </div>

        {/* Track */}
        <div
          ref={trackRef}
          className={`pricing-guidance__track${
            isDragging ? ' pricing-guidance__track--dragging' : ''
          }`}
          role="slider"
          aria-valuemin={minPrice}
          aria-valuemax={maxPrice}
          aria-valuenow={effectiveValue}
          aria-label="Listing price"
          tabIndex={0}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onKeyDown={handleKeyDown}
        >
          <div
            className="pricing-guidance__handle"
            style={{ left: `${pct}%` }}
            aria-hidden="true"
          />
        </div>

        {/* Last sold marker */}
        <div
          className="pricing-guidance__last-sold-marker"
          style={{ left: `${lastSoldPct}%` }}
          aria-hidden="true"
        >
          <div className={`pricing-guidance__last-sold-tick${isSnapped ? ' pricing-guidance__last-sold-tick--emphasized' : ''}`} />
          <span className={`pricing-guidance__last-sold-label${isSnapped ? ' pricing-guidance__last-sold-label--emphasized' : ''}`}>Last sold</span>
        </div>
      </div>

      {/* Bottom labels */}
      <div className="pricing-guidance__bottom">
        <span className="pricing-guidance__bottom-label">
          {formatPrice(sellQuickerPrice, { currencyCode })} / Sell Quicker
        </span>
        <span className="pricing-guidance__bottom-label">
          {formatPrice(maxPrice, { currencyCode })} / Earn More
        </span>
      </div>
    </div>
  )
}

export default PricingGuidanceSlider
